import React, { useEffect, useState } from "react";
import FieldErrorMessage from "../../Utils/FieldErrorMessage";
import { FaRegEdit } from "react-icons/fa";
import { FiRefreshCw } from "react-icons/fi";
import {
  authToken,
  isAdmin,
  translateToBanglaDigit,
  url,
} from "../../../helpers/helpers";
import axios from "axios";
import Pagination from "../../Utils/Pagination";
import Select2Wrapper from "../../Utils/Select2Wrapper";

const Institution = ({
  handleSubmit,
  onSubmit,
  errors,
  register,
  setValue,
  data,
  currentPage,
  totalPages,
  setCurrentPage,
  from,
  usrList,
  getUserList,
  watch,
  getMinistryOrganizationAndInstitution,
  parents,
  handleDelete,
  handleEdit,
  permissions,
  listLoading = false,
  saving = false,
}) => {
  const actionLoading = listLoading || saving;
  const [institution, setInstitution] = useState([]);

  useEffect(() => {
    setValue("type", "institution");
  }, []);

  useEffect(() => {
    if (data && data.length) {
      setInstitution([...data]);
    }
  }, [data]);

  const [filteredParents, setFilteredParents] = useState(parents);
  const [selectedParent, setSelectedParent] = useState("");
  const selectedMinistryType = watch("ministry_type");
  const [selectedMinistrySearch, setSelectedMinistrySearch] = useState("");

  // Geo states for form (left panel)
  const [divisions, setDivisions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [upazilas, setUpazilas] = useState([]);

  // Geo states for filter panel (right panel)
  const [filterDivisions, setFilterDivisions] = useState([]);
  const [filterDistricts, setFilterDistricts] = useState([]);
  const [filterUpazilas, setFilterUpazilas] = useState([]);
  const [filterDivisionId, setFilterDivisionId] = useState("");
  const [filterDistrictId, setFilterDistrictId] = useState("");
  const [filterUpazillaId, setFilterUpazillaId] = useState("");

  // Watch form values for cascading selects
  const selectedDivisionId = watch("division_id");
  const selectedDistrictId = watch("district_id");
  const selectedUpazillaId = watch("upazilla_id");
  const selectedParentId = watch("parent_id");
  const selectedHeadOfOrganization = watch("head_of_organization");
  const userOptions = usrList?.map((user) => {
    const name = user?.name?.trim();
    return {
      value: user.id,
      label: name ? `${name} (${user.unique_id})` : user.unique_id,
    };
  }) || [];

  const fetchDistricts = (divisionId) => {
    if (!divisionId) {
      setDistricts([]);
      return Promise.resolve([]);
    }

    return axios
      .get(url(`fetch-district/${divisionId}`), {
        headers: { Authorization: `Bearer ${authToken()}` },
      })
      .then((res) => {
        const data = res.data?.data || res.data || [];
        setDistricts(data);
        return data;
      })
      .catch((err) => {
        console.error("Failed to fetch districts", err);
        return [];
      });
  };

  const fetchUpazilas = (districtId) => {
    if (!districtId) {
      setUpazilas([]);
      return Promise.resolve([]);
    }

    return axios
      .get(url(`fetch-upazilla/${districtId}`), {
        headers: { Authorization: `Bearer ${authToken()}` },
      })
      .then((res) => {
        const data = res.data?.data || res.data || [];
        setUpazilas(data);
        return data;
      })
      .catch((err) => {
        console.error("Failed to fetch upazilas", err);
        return [];
      });
  };

  const handleInstitutionEdit = async (institutionInfo) => {
    const divisionId = institutionInfo?.division_id || "";
    const districtId = institutionInfo?.district_id || "";
    const upazillaId = institutionInfo?.upazilla_id || "";

    setDistricts([]);
    setUpazilas([]);

    if (divisionId) {
      await fetchDistricts(divisionId);
    }

    if (districtId) {
      await fetchUpazilas(districtId);
    }

    handleEdit(institutionInfo);
    setValue("division_id", divisionId);
    setValue("district_id", districtId);
    setValue("upazilla_id", upazillaId);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleChangeMinistrySearch = (value) => {
    setSelectedMinistrySearch(value);
  };
  const handleChange = (value) => {
    setSelectedParent(value);
  };

  useEffect(() => {
    if (selectedMinistryType) {
      const filteredMinistryType = parents.filter(
        (parent) => parent.ministry_type === parseInt(selectedMinistryType),
      );
      setFilteredParents(filteredMinistryType);
    } else {
      setFilteredParents(parents);
    }
  }, [selectedMinistryType, parents]);

  useEffect(() => {
    getMinistryOrganizationAndInstitution(
      "institution",
      selectedParent,
      selectedMinistrySearch,
      filterDivisionId,
      filterDistrictId,
      filterUpazillaId,
    );
  }, [selectedParent, currentPage, selectedMinistrySearch, filterDivisionId, filterDistrictId, filterUpazillaId]);

  // Fetch all divisions once on mount (shared by form + filter)
  useEffect(() => {
    axios
      .get(url("fetch-division"), {
        headers: { Authorization: `Bearer ${authToken()}` },
      })
      .then((res) => {
        const data = res.data?.data || res.data || [];
        setDivisions(data);
        setFilterDivisions(data);
      })
      .catch((err) => console.error("Failed to fetch divisions", err));
  }, []);

  // Fetch districts when form division changes
  useEffect(() => {
    if (selectedDivisionId) {
      fetchDistricts(selectedDivisionId);
    } else {
      setDistricts([]);
      setUpazilas([]);
    }
  }, [selectedDivisionId]);

  // Fetch upazilas when form district changes
  useEffect(() => {
    if (selectedDistrictId) {
      fetchUpazilas(selectedDistrictId);
    } else {
      setUpazilas([]);
    }
  }, [selectedDistrictId]);

  // Fetch districts for filter panel when filter division changes
  useEffect(() => {
    if (filterDivisionId) {
      setFilterDistricts([]);
      setFilterUpazilas([]);
      setFilterDistrictId("");
      axios
        .get(url(`fetch-district/${filterDivisionId}`), {
          headers: { Authorization: `Bearer ${authToken()}` },
        })
        .then((res) => {
          const data = res.data?.data || res.data || [];
          setFilterDistricts(data);
        })
        .catch((err) => console.error("Failed to fetch filter districts", err));
    } else {
      setFilterDistricts([]);
      setFilterUpazilas([]);
    }
  }, [filterDivisionId]);

  // Fetch upazilas for filter panel when filter district changes
  useEffect(() => {
    if (filterDistrictId) {
      setFilterUpazilas([]);
      setFilterUpazillaId("");
      axios
        .get(url(`fetch-upazilla/${filterDistrictId}`), {
          headers: { Authorization: `Bearer ${authToken()}` },
        })
        .then((res) => {
          const data = res.data?.data || res.data || [];
          setFilterUpazilas(data);
        })
        .catch((err) => console.error("Failed to fetch filter upazilas", err));
    } else {
      setFilterUpazilas([]);
    }
  }, [filterDistrictId]);
  const totalEmployeeData = (institutes = [], property = null) => {
    return institutes.reduce(
      (total, ins) =>
        total + (parseInt(ins[property]) > 0 ? parseInt(ins[property]) : 0),
      0,
    );
  };

  const divisionField = register("division_id");
  const districtField = register("district_id");
  const upazillaField = register("upazilla_id");
  const parentField = register("parent_id");

  const handleParentChange = (event) => {
    parentField.onChange(event);
    setValue("head_of_organization", "");
    getUserList({organization_id: event.target.value});
  };

  return (
    <>
      <div className="card shadow border-0 mt-2">
        <div className="card-body">
          <div className="row">
            <div className="col-4">
              <form onSubmit={handleSubmit(onSubmit)}>
                <fieldset disabled={actionLoading}>
                <div className="row">
                  <div className="col-12">
                    <label htmlFor="ministry" className="form-label">
                      মন্ত্রণালয়ের ধরন <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      aria-label="Default select example"
                      {...register("ministry_type")}
                    >
                      <option value="">মন্ত্রণালয় নির্বাচন করুন</option>
                      <option value="1">শিল্প মন্ত্রণালয়</option>
                      <option value="2">অন্যান্য মন্ত্রণালয়</option>
                    </select>
                    <FieldErrorMessage errors={errors} name="ministry_type" />
                  </div>
                  <div className="col-12 mt-2">
                    <label htmlFor="phone" className="form-label">
                      দপ্তর/সংস্থা <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      {...parentField}
                      onChange={handleParentChange}
                    >
                      <option value="" style={{ color: "gray" }}>
                        নির্বাচন করুন
                      </option>
                      {filteredParents &&
                        filteredParents.map((parent) => {
                          return (
                            <option key={parent.id} value={parent.id}>
                              {parent.name}
                            </option>
                          );
                        })}
                    </select>
                    <FieldErrorMessage errors={errors} name="parent_id" />
                  </div>
                  <div className="col-12 mt-2">
                    <label htmlFor="name" className="form-label">
                      নাম <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      {...register("name")}
                    />
                    <FieldErrorMessage errors={errors} name="name" />
                  </div>

                  {/* division */}
                  <div className="col-12 mt-2">
                    <label htmlFor="division_id" className="form-label">
                      বিভাগ <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      {...divisionField}
                      value={selectedDivisionId || ""}
                      onChange={(event) => {
                        divisionField.onChange(event);
                        setValue("division_id", event.target.value);
                        setValue("district_id", "");
                        setValue("upazilla_id", "");
                        setDistricts([]);
                        setUpazilas([]);
                      }}
                    >
                      <option value="" style={{ color: "gray" }}>
                        নির্বাচন করুন
                      </option>
                      {divisions.map((division) => (
                        <option key={division.id} value={division.id}>
                          {division.label || division.name}
                        </option>
                      ))}
                    </select>
                    <FieldErrorMessage errors={errors} name="division_id" />
                  </div>
                  {/* district */}
                  <div className="col-12 mt-2">
                    <label htmlFor="district_id" className="form-label">
                      জেলা <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      {...districtField}
                      value={selectedDistrictId || ""}
                      onChange={(event) => {
                        districtField.onChange(event);
                        setValue("district_id", event.target.value);
                        setValue("upazilla_id", "");
                        setUpazilas([]);
                      }}
                      disabled={!selectedDivisionId || districts.length === 0}
                    >
                      <option value="" style={{ color: "gray" }}>
                        নির্বাচন করুন
                      </option>
                      {districts.map((district) => (
                        <option key={district.id} value={district.id}>
                          {district.bn || district.text}
                        </option>
                      ))}
                    </select>
                    <FieldErrorMessage errors={errors} name="district_id" />
                  </div>
                  {/* upazilla */}
                  <div className="col-12 mt-2">
                    <label htmlFor="upazilla_id" className="form-label">
                      উপজেলা <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      {...upazillaField}
                      value={selectedUpazillaId || ""}
                      onChange={(event) => {
                        upazillaField.onChange(event);
                        setValue("upazilla_id", event.target.value);
                      }}
                      disabled={!selectedDistrictId || upazilas.length === 0}
                    >
                      <option value="" style={{ color: "gray" }}>
                        নির্বাচন করুন
                      </option>
                      {upazilas.map((upazilla) => (
                        <option key={upazilla.id} value={upazilla.id}>
                          {upazilla.bn || upazilla.text}
                        </option>
                      ))}
                    </select>
                    <FieldErrorMessage errors={errors} name="upazilla_id" />
                  </div>

                  <div className="col-12 mt-2">
                    <label htmlFor="location" className="form-label">
                      অবস্থান <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      {...register("location")}
                    />
                    <FieldErrorMessage errors={errors} name="location" />
                  </div>

                  <div className="col-12 mt-2">
                    <label htmlFor="phone" className="form-label">
                      প্রতিষ্ঠান প্রধান <span className="text-danger">*</span>
                    </label>
                    <Select2Wrapper
                      key={`head-of-organization-${selectedParentId || "all"}`}
                      value={selectedHeadOfOrganization}
                      placeholder="নির্বাচন করুন"
                      onChange={(value) => setValue("head_of_organization", value)}
                      options={userOptions}
                    />
                    <FieldErrorMessage
                      errors={errors}
                      name="head_of_organization"
                    />
                  </div>

                  <div className="col-12 mt-2">
                    <label htmlFor="total_employee" className="form-label">
                      মোট জনবল
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      {...register("total_employee")}
                    />
                    <FieldErrorMessage errors={errors} name="total_employee" />
                  </div>
                  <div className="col-12 mt-4">
                    <div className="form-group">
                      <input
                        id="is_head_office"
                        style={{ marginRight: "8px" }}
                        type="checkbox"
                        {...register("is_head_office")}
                      />
                      <label htmlFor="is_head_office">প্রধান কার্যালয়</label>
                    </div>
                  </div>
                </div>
                <br />
                <button className="btn btn-sm btn-success">
                  {saving && <span className="spinner me-2"></span>}
                  সংরক্ষণ করুন
                </button>
                </fieldset>
              </form>
            </div>

            
            <div className="col-8">
              <div className="row">
                <div className="col-4">
              <select
                className="form-select"
                disabled={actionLoading}
                onChange={(e) => handleChangeMinistrySearch(e.target.value)}
              >
                    <option value="">মন্ত্রণালয় নির্বাচন করুন</option>
                    <option value="1">শিল্প মন্ত্রণালয়</option>
                    <option value="2">অন্যান্য মন্ত্রণালয়</option>
                  </select>
                </div>
                <div className="col-4">
              <select
                className="form-select"
                disabled={actionLoading}
                onChange={(e) => handleChange(e.target.value)}
              >
                    <option value="" style={{ color: "gray" }}>
                      দপ্তর/সংস্থা নির্বাচন করুন
                    </option>
                    {parents &&
                      parents.map((parent) => {
                        return (
                          <option key={parent.id} value={parent.id}>
                            {parent.name}
                          </option>
                        );
                      })}
                  </select>
                </div>
                <div className="col-4">
                  <select
                className="form-select"
                value={filterDivisionId}
                disabled={actionLoading}
                onChange={(e) => setFilterDivisionId(e.target.value)}
                  >
                    <option value="" style={{ color: "gray" }}>
                      বিভাগ
                    </option>
                    {filterDivisions.map((division) => (
                      <option key={division.id} value={division.id}>
                        {division.label || division.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-4 mt-2">
                  <select
                className="form-select"
                value={filterDistrictId}
                onChange={(e) => setFilterDistrictId(e.target.value)}
                disabled={actionLoading || !filterDivisionId || filterDistricts.length === 0}
                  >
                    <option value="" style={{ color: "gray" }}>
                      জেলা
                    </option>
                    {filterDistricts.map((district) => (
                      <option key={district.id} value={district.id}>
                        {district.bn || district.text}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-4 mt-2">
                  <select
                  className="form-select"
                  value={filterUpazillaId}
                  disabled={actionLoading || !filterDistrictId || filterUpazilas.length === 0}
                  onChange={(e) => setFilterUpazillaId(e.target.value)}
                    >
                    <option value="" style={{ color: "gray" }}>
                      উপজেলা
                    </option>
                    {filterUpazilas.map((upazilla) => (
                      <option key={upazilla.id} value={upazilla.id}>
                        {upazilla.bn || upazilla.text}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-4 mt-2">
                  <button
                    className="btn btn-warning"
                    onClick={() => {
                      setSelectedMinistrySearch("");
                      setSelectedParent("");
                      setFilterDivisionId("");
                      setFilterDistrictId("");
                      setFilterUpazillaId("");
                    }}
                    disabled={actionLoading}
                  >
                   <FiRefreshCw /> রিসেট
                  </button>
                </div>
              </div>
              <div className="table-responsive pt-4">
                <table className="table table-bordered table-hover">
                  <thead>
                    <tr style={{ backgroundColor: "#f9f9f9" }}>
                      <th scope="col" style={{ textAlign: "center" }}>
                        ক্রমিক নং
                      </th>
                      <th scope="col">নাম</th>
                      <th scope="col">দপ্তর/সংস্থা</th>
                      <th scope="col">এন্ট্রিকৃত ডাটা</th>
                      <th scope="col">মোট জনবল</th>
                      <th scope="col">অবস্থান</th>
                      <th scope="col">প্রধানের নাম</th>
                      <th scope="col">মোবাইল</th>
                      <th scope="col">ইমেইল</th>
                      <th scope="col">প্রধান কার্যালয়</th>
                      <th scope="col">অ্যাকশন</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listLoading && (
                      <tr>
                        <td colSpan={11} className="text-center py-5">
                          <span className="spinner"></span>
                        </td>
                      </tr>
                    )}
                    {!listLoading && data &&
                      data.map((ministry, index) => {
                        return (
                          <tr>
                            <td style={{ textAlign: "center" }}>
                              {translateToBanglaDigit(index + from)}
                            </td>
                            <td>{ministry.name}</td>
                            <td>{ministry.parent?.name}</td>
                            <td>
                              {translateToBanglaDigit(
                                ministry?.institute_employees_count,
                              )}
                            </td>
                            <td>
                              {translateToBanglaDigit(ministry?.total_employee)}
                            </td>
                            <td>{ministry?.location}</td>
                            <td>{ministry?.organization_head?.name}</td>
                            <td>{ministry?.organization_head?.phone}</td>
                            <td>{ministry?.organization_head?.email}</td>
                            <td>
                              {ministry.is_head_office == 1 && (
                                <span className="badge bg-success">হ্যাঁ</span>
                              )}
                            </td>
                            <td>
                              <div>
                                {(isAdmin() ||
                                  permissions?.includes("edit")) && (
                                  <button
                                    className="btn btn-sm btn-secondary"
                                    disabled={actionLoading}
                                    onClick={() => handleInstitutionEdit(ministry)}
                                  >
                                    <FaRegEdit />
                                  </button>
                                )}

                                {/* &nbsp;
                                                            {
                                                                (isAdmin() || permissions?.includes('delete')) &&
                                                                <button className="btn btn-sm btn-secondary"
                                                                        onClick={() => handleDelete(ministry.id)}>
                                                                    <MdDeleteOutline/>
                                                                </button>
                                                            } */}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    {!listLoading && (
                      <tr>
                        <td style={{ textAlign: "right" }} colSpan={3}>
                          মোট
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {translateToBanglaDigit(
                            totalEmployeeData(data, "institute_employees_count"),
                          )}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {translateToBanglaDigit(
                            totalEmployeeData(data, "total_employee"),
                          )}
                        </td>
                        <td></td>
                        <td></td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  disabled={actionLoading}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Institution;
